import React, { useEffect, useRef, useState } from 'react';

export default function ProfessionChecker({
  onClose,
  // Keep props optional for future reuse, but we fetch internally by default.
  availablePositions = null,
  teammates = [],
  teamId = null,

  // NEW
  isCaptain = false,
  onAskTeam = null,
}) {



  const [positions, setPositions] = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [positionsError, setPositionsError] = useState('');
  const [teammateProfRows, setTeammateProfRows] = useState([]);

  // Hover toast (rollover preview)
  const [hoverPreview, setHoverPreview] = useState(null);
  const hoverCloseTimerRef = useRef(null);

  // Tiny dropdown for left-panel profession entries
  const [openAskMenuKey, setOpenAskMenuKey] = useState(null);

  useEffect(() => {
    if (openAskMenuKey == null) return;

    const onDocMouseDown = () => {
      setOpenAskMenuKey(null);
    };

    document.addEventListener('mousedown', onDocMouseDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
    };
  }, [openAskMenuKey]);


  const safeClearHoverTimer = () => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  };

  const getUserDisplayName = (row) => {
    const first = String(row?.first_name ?? row?.u_first_name ?? '').trim();
    const last = String(row?.last_name ?? row?.u_last_name ?? '').trim();
    const full = [first, last].filter(Boolean).join(' ').trim();
    return full || 'Unknown';
  };

  const getUserBusinessIndustry = (row) => {
    return String(row?.b_business_industry ?? '').trim();
  };

  const getUserProfilePicUrl = (row) => {
    return String(
      row?.avatar ||
      row?.u_profile_picture ||
      ''
    ).trim();
  };

const onEnterProfessionRow = (e, row) => {
    safeClearHoverTimer();

    // Now we use the data directly from the row (which comes from the API)
    // No need to match back to teammates - the API now returns all user data
    const hasUserData = row?.u_id != null;

    if (hasUserData) {
      console.log('[professionchecker] hovered user photo fields', {
        u_id: row?.u_id,
        first_name: row?.first_name,
        last_name: row?.last_name,
        u_first_name: row?.u_first_name,
        u_last_name: row?.u_last_name,
        avatar: row?.avatar,
        u_profile_picture: row?.u_profile_picture,
        b_business_industry: row?.b_business_industry,
      });
    }

    setHoverPreview({
          professionName: String(row?.profession_name ?? '').trim(),
          name: hasUserData ? getUserDisplayName(row) : '',
          businessName: hasUserData ? String(row?.b_name ?? '').trim() : '',
          phone: hasUserData ? String(row?.b_phone ?? '').trim() : '',
          industryRaw: hasUserData ? getUserBusinessIndustry(row) : '',
          photoUrl: hasUserData ? getUserProfilePicUrl(row) : '',
        });
  };

  const onLeaveProfessionRow = () => {
    safeClearHoverTimer();

    // Small delay so it behaves like a "preview toast" instead of flickering
    hoverCloseTimerRef.current = setTimeout(() => {
      setHoverPreview(null);
    }, 120);
  };


  useEffect(() => {
    // If positions were explicitly provided, prefer them and skip fetch.
    if (Array.isArray(availablePositions)) {
      setPositions(availablePositions);
      setPositionsError('');
      setPositionsLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setPositionsLoading(true);
        setPositionsError('');

        const tidRaw = teamId;
        const tidNum = Number(tidRaw);

        const tid = Number.isFinite(tidNum) ? String(tidNum) : '';
        const url = tid
          ? `/api/analysis/profession-canonical?team_id=${encodeURIComponent(tid)}`
          : `/api/analysis/profession-canonical`;

        console.log('[ProfessionChecker] teamId debug', {
          teamIdProp: tidRaw,
          teamIdAsNumber: tidNum,
          url
        });


        const res = await fetch(url, {
          credentials: 'include',
        });

        const json = await res.json().catch(() => ({}));

        if (!res.ok || !json?.ok) {
          throw new Error(json?.error || `HTTP ${res.status}`);
        }

        const list = Array.isArray(json?.professions) ? json.professions : [];

        const rows = list
          .map((r) => {
            const idRaw = r?.profession_canonical_id;
            const idNum = Number(idRaw);
            const id = Number.isFinite(idNum) ? idNum : null;

            const name = String(r?.profession_name ?? '').trim();
            if (!name) return null;

            return {
              profession_canonical_id: id,
              profession_name: name,
            };
          })
          .filter(Boolean);

        if (!cancelled) setPositions(rows);

      } catch (e) {
        if (!cancelled) {
          setPositions([]);
          setPositionsError(String(e?.message || e || 'load_failed'));
        }
      } finally {
        if (!cancelled) setPositionsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [availablePositions, teamId]);

useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        if (!teamId) {
          if (!cancelled) setTeammateProfRows([]);
          return;
        }

        // Fetch team members with their b_ids from the database
        const teamRes = await fetch(`/api/analysis/teams/${teamId}/members-with-businesses`, {
          credentials: 'include',
        });

        if (!teamRes.ok) {
          throw new Error(`HTTP ${teamRes.status}`);
        }

        const teamJson = await teamRes.json();
        const b_ids = Array.isArray(teamJson?.b_ids)
          ? teamJson.b_ids.filter(id => Number.isFinite(Number(id)))
          : [];
        const members = Array.isArray(teamJson?.members) ? teamJson.members : [];

        console.log('[professionchecker] members from database', {
          teamId,
          members_count: members.length,
          b_ids,
        });

        if (b_ids.length === 0) {
          if (!cancelled) setTeammateProfRows([]);
          return;
        }

        // Now use the existing teammate-professions route
        // Pass members (u_id+b_id pairs) for accurate user resolution
        const res = await fetch('/api/analysis/teammate-professions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ b_ids, members, team_id: teamId }),
        });

        const json = await res.json().catch(() => ({}));

        if (!res.ok || !json?.ok) {
          throw new Error(json?.error || `HTTP ${res.status}`);
        }

        const rows = Array.isArray(json?.rows) ? json.rows : [];

        console.log('[professionchecker] teammateProfRows response', {
          teamId,
          requested: b_ids.length,
          returned: rows.length,
          rows,
        });

        if (!cancelled) setTeammateProfRows(rows);

      } catch (e) {
        console.log('[professionchecker] error:', String(e?.message || e));
        if (!cancelled) setTeammateProfRows([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [teamId]);


  const occupiedIds = new Set(
    (Array.isArray(teammateProfRows) ? teammateProfRows : [])
      .map((r) => Number(r?.profession_canonical_id))
      .filter((n) => Number.isFinite(n))
  );


  useEffect(() => {
    const first = Array.isArray(teammateProfRows) && teammateProfRows.length > 0 ? teammateProfRows[0] : null;

    console.log('[professionchecker] roster debug', {
      teamId,
      teammateProfRows_len: Array.isArray(teammateProfRows) ? teammateProfRows.length : null,
      first_keys: first ? Object.keys(first).slice(0, 40) : [],
      occupied_count: occupiedIds.size,
      occupied_sample: Array.from(occupiedIds).slice(0, 10),
    });
  }, [teamId, teammateProfRows, occupiedIds]);


  const positionNameById = new Map(
    (Array.isArray(positions) ? positions : [])
      .map((p) => [Number(p?.profession_canonical_id), String(p?.profession_name ?? '').trim()])
      .filter(([id, name]) => Number.isFinite(id) && name)
  );




  return (
    <div
    className="absolute inset-0 z-[999] flex items-start justify-center pt-[50px]"
    onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
    }}
    >

      {/* Backdrop */}
      <div className="absolute inset-0" />

      {/* Modal */}
      <div className="relative w-1/2 h-1/2 min-w-[550px] min-h-[480px] bg-white rounded-2xl shadow-2xl overflow-hidden">

        {/* Hover Preview Toast */}
        {hoverPreview ? (
<div
            className="fixed z-[99999] rounded-xl shadow-2xl overflow-hidden bg-white"
            style={{
              width: 180,
              // Fixed position: left side of the modal, vertically centered
              top: '50%',
              left: '33%',
              transform: 'translate(-50%, -50%)',
            }}
            onMouseEnter={() => {
              // If they drift onto the toast, keep it open
              safeClearHoverTimer();
            }}
            onMouseLeave={onLeaveProfessionRow}
          >
            {/* Image: square + full width, edge-to-edge */}
            <div className="w-full aspect-square bg-gray-100">
              {hoverPreview.photoUrl ? (
                <img
                  src={hoverPreview.photoUrl}
                  alt=""
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                  No photo
                </div>
              )}
            </div>
            {/* White info block under image */}
            <div className="bg-white px-3 py-2">
              <div className="text-sm font-semibold text-gray-900 leading-snug">
                {hoverPreview.name || ' '}
              </div>
              {hoverPreview.businessName && (
                <div className="text-xs text-gray-700 leading-snug mt-1">
                  {hoverPreview.businessName}
                </div>
              )}
              {hoverPreview.phone && (
                <div className="text-xs text-gray-600 leading-snug mt-0.5">
                  {hoverPreview.phone}
                </div>
              )}
              <div className="text-xs text-gray-600 leading-snug mt-0.5">
                {hoverPreview.industryRaw || ' '}
              </div>
            </div>
          </div>
        ) : null}


        {/* Header */}
        <div className="flex items-center justify-between px-4 py-0 bg-[#00aef7]">
        <div className="text-sm font-semibold text-white tracking-wide">
            Roster by Profession
        </div>

        <button
            type="button"
            className="inline-flex items-center justify-center rounded-md px-2 py-1 text-white hover:bg-white/10"
            aria-label="Close Profession Checker"
            title="Close"
            onClick={onClose}
        >
            ✕
        </button>
        </div>


        {/* Body: 2 columns + divider */}
        <div className="w-full h-full flex min-h-0">
          {/* Left column */}
          <div className="w-1/2 flex flex-col min-h-0">
            <div className="px-3 py-2 border-b border-gray-200 bg-white flex justify-center">
            <div className="text-xs font-semibold tracking-wide uppercase text-[#00aef7]">
                Available Positions
            </div>
            </div>
            <div className="px-3 pt-2 pb-[60px] flex-1 min-h-0 overflow-y-auto text-sm scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-100">
              {positionsLoading ? (
                <div className="text-sm text-gray-500 px-2 py-1">
                  Loading...
                </div>
              ) : positionsError ? (
                <div className="text-sm text-red-600 px-2 py-1">
                  {positionsError}
                </div>
              ) : Array.isArray(positions) && positions.length > 0 ? (
                <ul className="space-y-1">
                  {positions.map((p) => {
                    const idNum = Number(p?.profession_canonical_id);
                    const id = Number.isFinite(idNum) ? idNum : null;
                    const name = String(p?.profession_name ?? '').trim();

                    const isOccupied = id != null && occupiedIds.has(id);

                    const menuKey = id ?? name;
                    const isOpen = openAskMenuKey === menuKey;

                    return (
                      <li
                        key={menuKey}
                        className={`px-2 py-1 rounded ${
                          isOccupied ? 'text-gray-400' : 'text-gray-800'
                        }`}
                      >
                        <div className="relative inline-block w-full">
                          <button
                            type="button"
                            disabled={isOccupied}
                            onMouseDown={(e) => {
                              e.stopPropagation(); // prevents the document handler from immediately closing it
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isOccupied) return; // filled position: do nothing
                              if (!isCaptain) return; // non-captain: do nothing
                              setOpenAskMenuKey((prev) => (prev === menuKey ? null : menuKey));
                            }}
                            className={`w-full text-left rounded px-0 py-0 ${
                              isOccupied
                                ? 'cursor-default'
                                : isCaptain
                                  ? 'hover:bg-gray-100 cursor-pointer'
                                  : 'cursor-default'
                            }`}
                          >
                            {name}
                          </button>



                          {isOpen && (
                            <div
                              className="absolute right-0 top-full mt-[1px] bg-white border border-gray-200 rounded shadow-sm z-50"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                            >

                              <button
                                type="button"
                                className="block w-full text-left px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 whitespace-nowrap"
                                onClick={() => {
                                  setOpenAskMenuKey(null);

                                  if (!isCaptain) return;

                                  // Close ProfessionChecker first so ChatCenter can win the global z-stack
                                  if (typeof onClose === 'function') onClose();

                                  if (typeof onAskTeam === 'function') {
                                    onAskTeam({
                                      professionName: name,
                                      professionCanonicalId: id,
                                    });
                                  }
                                }}
                              >
                                Ask the Team
                              </button>


                            </div>
                          )}
                        </div>
                      </li>
                    );

                  })}
                </ul>
              ) : (
                <div className="text-sm text-gray-500 px-2 py-1">
                  No positions available.
                </div>
              )}
            </div>

          </div>

          {/* Divider */}
          <div className="w-px bg-gray-200" />

          {/* Right column */}
          <div className="w-1/2 flex flex-col min-h-0">
            <div className="px-3 py-2 border-b border-gray-200 bg-white flex justify-center">
            <div className="text-xs font-semibold tracking-wide uppercase text-[#00aef7]">
                Teammates
            </div>
            </div>


            <div className="px-3 pb-3 pt-2 flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-100">
              {Array.isArray(teammateProfRows) && teammateProfRows.length > 0 ? (
                <ul className="space-y-1">
                  {[...teammateProfRows]
                    .map((r, idx) => ({
                      key:
                        Number.isFinite(Number(r?.b_id)) && Number.isFinite(Number(r?.profession_canonical_id))
                          ? `b${Number(r?.b_id)}_p${Number(r?.profession_canonical_id)}`
                          : `row_${idx}`,
                      b_id: r?.b_id,
                      u_id: r?.u_id,
                      profession_canonical_id: r?.profession_canonical_id,
                      profession_name: String(r?.profession_name ?? '').trim(),
                      first_name: r?.first_name,
                      last_name: r?.last_name,
                      u_first_name: r?.u_first_name,
                      u_last_name: r?.u_last_name,
                      avatar: r?.avatar,
                      u_profile_picture: r?.u_profile_picture,
                      b_name: r?.b_name,
                      b_phone: r?.b_phone,
                      b_business_industry: r?.b_business_industry,
                      _raw: r,
                    }))
                    .filter((x) => x.profession_name)
                    .sort((a, b) => a.profession_name.localeCompare(b.profession_name))
                    .map((row) => (
                      <li
                        key={row.key}
                        className="text-sm px-2 py-1 rounded-md"
                        style={{ color: "#00aef7" }}
                        onMouseEnter={(e) => onEnterProfessionRow(e, row)}
                        onMouseLeave={onLeaveProfessionRow}
                      >
                        {/* No underline, no link styling, UI stays the same */}
                        <span className="select-none">{row.profession_name}</span>
                      </li>
                    ))}
                </ul>
              ) : (


                <div className="text-sm text-gray-500 px-2 py-1">
                  No teammates loaded.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}